
import React, { useState, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { ChevronRight, ChevronDown } from "lucide-react";
import { Group } from '@/types/note';

interface HierarchicalGroupSelectorProps {
  selectedGroup: string;
  groups: Group[];
  onGroupChange: (value: string) => void;
}

const HierarchicalGroupSelector: React.FC<HierarchicalGroupSelectorProps> = ({
  selectedGroup,
  groups,
  onGroupChange
}) => {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [isOpen, setIsOpen] = useState(false);

  // Build hierarchical structure
  const buildHierarchy = () => {
    const hierarchy: { [key: string]: Group[] } = {};
    const topLevelGroups: Group[] = [];

    groups.forEach(group => {
      if (!group.parent_id && !group.name.includes(' > ')) {
        // This is a top-level group
        topLevelGroups.push(group);
      } else if (group.name.includes(' > ')) {
        // This is a subgroup using naming convention
        const parts = group.name.split(' > ');
        if (parts.length === 2) {
          // Direct subgroup of top-level group
          const parentName = parts[0];
          const parentGroup = groups.find(g => g.name === parentName);
          if (parentGroup) {
            if (!hierarchy[parentGroup.id]) {
              hierarchy[parentGroup.id] = [];
            }
            hierarchy[parentGroup.id].push(group);
          }
        } else if (parts.length > 2) {
          // Sub-subgroup
          const parentPath = parts.slice(0, -1).join(' > ');
          const parentGroup = groups.find(g => g.name === parentPath);
          if (parentGroup) {
            if (!hierarchy[parentGroup.id]) {
              hierarchy[parentGroup.id] = [];
            }
            hierarchy[parentGroup.id].push(group);
          }
        }
      } else if (group.parent_id) {
        // Using parent_id relationship
        if (!hierarchy[group.parent_id]) {
          hierarchy[group.parent_id] = [];
        }
        hierarchy[group.parent_id].push(group);
      }
    });

    return { topLevelGroups, hierarchy };
  };

  const { topLevelGroups, hierarchy } = buildHierarchy();

  const toggleExpanded = (groupId: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  const handleGroupSelect = (groupId: string) => {
    onGroupChange(groupId);
    setIsOpen(false);
  };

  const getDisplayName = (fullName: string) => {
    const parts = fullName.split(' > ');
    return parts[parts.length - 1];
  };

  const getSelectedGroupName = () => {
    if (selectedGroup === "none") return "No Group";
    const group = groups.find(g => g.id === selectedGroup);
    return group ? getDisplayName(group.name) : "Select group";
  };

  // Generate a color based on group name for consistency
  const getGroupColor = (groupName: string) => {
    const colors = [
      '#F87171', '#FB923C', '#FBBF24', '#A3E635', '#34D399',
      '#22D3EE', '#60A5FA', '#A78BFA', '#F472B6', '#FB7185'
    ];
    let hash = 0;
    for (let i = 0; i < groupName.length; i++) {
      hash = groupName.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const isTopLevelGroup = (group: Group) => {
    return !group.parent_id && !group.name.includes(' > ');
  };

  const renderGroupItem = (group: Group, level: number = 0) => {
    const hasChildren = hierarchy[group.id] && hierarchy[group.id].length > 0;
    const isExpanded = expandedGroups.has(group.id);
    const paddingLeft = level * 16;
    const isTopLevel = isTopLevelGroup(group);

    return (
      <div key={group.id}>
        <div
          className="flex items-center justify-between hover:bg-accent hover:text-accent-foreground px-2 py-1.5 cursor-pointer"
          style={{ paddingLeft: paddingLeft + 8 }}
        >
          <div className="flex items-center gap-2 flex-1">
            {hasChildren && (
              <Button
                variant="ghost"
                size="sm"
                className="h-4 w-4 p-0 hover:bg-transparent"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpanded(group.id);
                }}
              >
                {isExpanded ? (
                  <ChevronDown className="h-3 w-3" />
                ) : (
                  <ChevronRight className="h-3 w-3" />
                )}
              </Button>
            )}
            {!hasChildren && <div className="w-4" />}
            
            {!isTopLevel && (
              <div 
                className="w-2 h-2 rounded-full mr-1"
                style={{ backgroundColor: getGroupColor(group.name) }}
              />
            )}
            
            <span
              className={`text-sm flex-1 ${isTopLevel ? 'font-bold' : 'font-normal'}`}
              onClick={() => handleGroupSelect(group.id)}
            >
              {getDisplayName(group.name)}
            </span>
          </div>
        </div>
        {hasChildren && isExpanded && (
          <div>
            {hierarchy[group.id].map((child) => renderGroupItem(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="relative">
      <Select
        value={selectedGroup}
        onValueChange={onGroupChange}
        open={isOpen}
        onOpenChange={setIsOpen}
      >
        <SelectTrigger className="w-[200px]" data-tour="group-selector">
          <SelectValue placeholder="Select group">
            {getSelectedGroupName()}
          </SelectValue>
        </SelectTrigger>
        <SelectContent 
          className="w-[200px] p-0"
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <div
            className="flex items-center hover:bg-accent hover:text-accent-foreground px-2 py-1.5 cursor-pointer font-bold"
            onClick={() => handleGroupSelect("none")}
          >
            <span className="text-sm">No Group</span>
          </div>
          {topLevelGroups.map((group) => renderGroupItem(group))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default HierarchicalGroupSelector;
